import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Home,
  MessageSquare,
  Wrench,
  FileText,
  Settings,
  LogOut,
  Bell,
  TrendingUp,
  Users,
  Hammer,
  Menu,
  X,
  Building2,
  ChevronDown,
  Check
} from 'lucide-react';
import { getApiUrl } from '../config/api';
import authFetch, { parseToken } from '../utils/authFetch';
import { roleLabel } from '../utils/roles';
import switchOrganization from '../utils/switchOrganization';
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
} from './ui/dropdown-menu';
import useIsMobile from '../hooks/useIsMobile';

const Layout = ({ children, pageTitle, pageSubtitle }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [unreadCount, setUnreadCount] = useState(0);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  // Org switcher: only rendered for users with more than one membership.
  const [organizations, setOrganizations] = useState([]);
  const [orgMenuOpen, setOrgMenuOpen] = useState(false);
  const [switchingOrg, setSwitchingOrg] = useState(false);
  const [switchError, setSwitchError] = useState('');
  // The org the caller is currently acting in, taken from the access token.
  const activeOrgId = parseToken(localStorage.getItem('access_token'))?.org_id ?? null;
  // Track mobile viewport so the off-screen drawer can be made inert (removed
  // from tab order and the accessibility tree) without affecting the
  // always-visible desktop sidebar. Boundary matches Tailwind's `md` breakpoint.
  const isMobile = useIsMobile('(max-width: 767.98px)');
  const navigate = useNavigate();
  const location = useLocation();

  const firstName = localStorage.getItem('first_name') || 'User';

  // Fetch unread message count
  const fetchUnreadCount = useCallback(async () => {
    try {
      const response = await authFetch(getApiUrl('/api/v1/message/unread-count'));

      if (response.ok) {
        const data = await response.json();
        setUnreadCount(data.unread_count || 0);
      }
    } catch (err) {
      console.error('Error fetching unread count:', err);
    }
  }, []);

  useEffect(() => {
    const checkAuth = () => {
      const token = localStorage.getItem('access_token');
      const payload = parseToken(token);
      if (!payload || Date.now() >= payload.exp * 1000) {
        setIsLoading(false);
        return navigate('/login');
      }
      setIsAuthenticated(true);
      setIsLoading(false);
    };

    checkAuth();
  }, [navigate]);

  // Fetch unread count when authenticated and on route change
  useEffect(() => {
    if (isAuthenticated) {
      fetchUnreadCount();
    }
  }, [isAuthenticated, location.pathname, fetchUnreadCount]);

  // Load the caller's organizations once authenticated. The switcher UI is only
  // shown when there is more than one membership.
  useEffect(() => {
    if (!isAuthenticated) return;

    const fetchOrganizations = async () => {
      try {
        const response = await authFetch(getApiUrl('/api/v1/organization/list'));
        if (response.ok) {
          const data = await response.json();
          setOrganizations(data.organizations || []);
        }
      } catch (err) {
        console.error('Error fetching organizations:', err);
      }
    };

    fetchOrganizations();
  }, [isAuthenticated]);

  // Switch active org: mint new tokens, persist them (same keys as login), then
  // do a full reload so every org-scoped view refetches under the new context.
  const handleSwitchOrg = async (organizationId) => {
    // Ignore re-entrant clicks (a second click before the reload) and no-op
    // selections of the org already in context.
    if (switchingOrg) return;
    if (organizationId === activeOrgId) {
      setOrgMenuOpen(false);
      return;
    }

    try {
      setSwitchingOrg(true);
      setSwitchError('');
      await switchOrganization(organizationId);
      window.location.reload();
    } catch (err) {
      // Keep the menu open so the inline error is visible instead of failing
      // silently.
      console.error('Error switching organization:', err);
      setSwitchingOrg(false);
      setSwitchError('Could not switch organizations. Please try again.');
    }
  };

  // Close the mobile sidebar drawer whenever the route changes
  useEffect(() => {
    setSidebarOpen(false);
  }, [location.pathname]);

  const handleNavigate = (path) => {
    if (path) navigate(path);
    setSidebarOpen(false);
  };

  const handleLogout = () => {
    localStorage.removeItem('access_token');
    localStorage.removeItem('refresh_token');
    localStorage.removeItem('user_id');
    localStorage.removeItem('first_name');
    setIsAuthenticated(false);
    navigate('/login');
  };

  // Navigation items
  const navItems = [
    { label: 'Dashboard', path: '/dashboard', icon: TrendingUp },
    { label: 'Properties', path: '/properties', icon: Home },
    { label: 'Tenants', path: '/tenants', icon: Users },
    { label: 'Contractors', path: '/contractors', icon: Hammer },
    { label: 'Appliances', path: '/maintenance', icon: Wrench },
    { label: 'Service Requests', path: '/service', icon: FileText },
    { label: 'Messages', path: '/messages', icon: MessageSquare },
    { label: 'Settings', path: '/settings', icon: Settings },
  ];

  // Check if current path matches nav item
  const isActive = (path) => {
    if (!path) return false;
    return location.pathname === path;
  };

  // The org currently in context (matched against the token's org_id claim),
  // falling back to the flagged default so the trigger always has a label.
  const currentOrg =
    organizations.find((o) => o.organization_id === activeOrgId) ||
    organizations.find((o) => o.is_default) ||
    null;

  if (isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[#F5F5F5]">
        <p className="text-[#7F8C8D]">Loading...</p>
      </div>
    );
  }

  if (!isAuthenticated) return null;

  return (
    <div className="min-h-screen bg-[#F5F5F5]">
      {/* Mobile backdrop - only visible when drawer is open */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 z-30 bg-black/50 md:hidden"
          onClick={() => setSidebarOpen(false)}
          aria-hidden="true"
        />
      )}

      {/* Sidebar - Fixed position; slides in/out on mobile, static on desktop.
          When off-screen on mobile it is inert so keyboard/screen-reader users
          can't reach it; on desktop it is always interactive. */}
      <aside
        inert={isMobile && !sidebarOpen}
        className={`fixed top-0 left-0 w-56 h-screen bg-[#2C3E50] text-white flex flex-col z-40 transition-transform duration-300 ease-in-out md:translate-x-0 ${
          sidebarOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
      >
        <div className="px-6 py-5 flex items-center justify-between">
          <button
            onClick={() => handleNavigate('/dashboard')}
            className="text-2xl font-bold text-white text-left rounded-lg focus:outline-none focus-visible:ring-2 focus-visible:ring-[#3498DB]"
            aria-label="Go to dashboard"
          >
            ZenRent
          </button>
          <button
            onClick={() => setSidebarOpen(false)}
            className="p-1 rounded-lg text-white/70 hover:bg-[#34495E] hover:text-white md:hidden"
            aria-label="Close menu"
          >
            <X size={22} />
          </button>
        </div>

        <nav className="flex-1 px-3 overflow-y-auto">
          {navItems.map((item) => {
            const Icon = item.icon;
            const active = isActive(item.path);

            return (
              <button
                key={item.label}
                onClick={() => handleNavigate(item.path)}
                className={`flex items-center gap-3 px-4 py-3 rounded-lg mb-1 w-full text-left transition-colors ${
                  active
                    ? 'bg-[#3498DB]'
                    : 'hover:bg-[#34495E]'
                }`}
              >
                <Icon size={20} />
                <span>{item.label}</span>
              </button>
            );
          })}
        </nav>

        <div className="p-3 border-t border-white/10 flex-shrink-0">
          <button
            onClick={handleLogout}
            className="flex items-center gap-3 px-4 py-3 rounded-lg text-white/70 hover:bg-[#C0392B] hover:text-white w-full transition-colors"
          >
            <LogOut size={20} />
            <span>Logout</span>
          </button>
        </div>
      </aside>

      {/* Main Content - left margin only on desktop for fixed sidebar.
          While the mobile drawer is open, the background is inert so keyboard
          users can't Tab past the drawer into the obscured content. */}
      <main className="md:ml-56 flex flex-col min-h-screen" inert={isMobile && sidebarOpen}>
        {/* Header */}
        <header className="bg-white border-b border-[#E2E8F0] px-4 md:px-8 py-5 flex-shrink-0">
          <div className="flex justify-between items-center">
            <div className="flex items-center gap-3">
              <button
                onClick={() => setSidebarOpen(true)}
                className="p-2 -ml-2 rounded-lg text-[#2C3E50] hover:bg-gray-100 transition-colors md:hidden"
                aria-label="Open menu"
              >
                <Menu size={24} />
              </button>
              <div>
                <h2 className="text-xl md:text-2xl font-bold text-[#2C3E50]">
                  {pageTitle || `Welcome back, ${firstName}!`}
                </h2>
                {pageSubtitle && (
                  <p className="text-sm text-[#7F8C8D] mt-1">{pageSubtitle}</p>
                )}
              </div>
            </div>
            <div className="flex items-center gap-3">
              {organizations.length > 1 && (
                <DropdownMenu
                  open={orgMenuOpen}
                  onOpenChange={(open) => {
                    setOrgMenuOpen(open);
                    if (open) setSwitchError('');
                  }}
                >
                  <DropdownMenuTrigger asChild>
                    <button
                      disabled={switchingOrg}
                      className="flex items-center gap-2 px-3 py-2 rounded-lg border border-[#E2E8F0] bg-white text-[#2C3E50] text-sm hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed max-w-[200px]"
                    >
                      <Building2 size={16} className="text-[#7F8C8D] shrink-0" />
                      <span className="truncate font-medium">{currentOrg?.org_name || 'Organization'}</span>
                      <ChevronDown size={16} className="text-[#7F8C8D] shrink-0" />
                    </button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-64">
                    <DropdownMenuLabel className="text-xs text-[#94A3B8] uppercase tracking-wide font-normal">
                      Switch organization
                    </DropdownMenuLabel>
                    {organizations.map((org) => {
                      const isCurrent = org.organization_id === activeOrgId;
                      return (
                        <DropdownMenuItem
                          key={org.organization_id}
                          disabled={switchingOrg}
                          // Drive closing ourselves so a failed switch keeps the
                          // menu open to show the inline error.
                          onSelect={(e) => {
                            e.preventDefault();
                            handleSwitchOrg(org.organization_id);
                          }}
                          className={`gap-2 px-3 py-2.5 cursor-pointer ${isCurrent ? 'bg-[#F1F5F9]' : ''}`}
                        >
                          <span className="w-4 shrink-0">
                            {isCurrent && <Check size={16} className="text-[#2ECC71]" />}
                          </span>
                          <span className="flex-1 min-w-0">
                            <span className="flex items-center gap-2">
                              <span className="text-sm text-[#2C3E50] font-medium truncate">
                                {org.org_name}
                              </span>
                              {org.is_default && (
                                <span className="text-[10px] px-1.5 py-0.5 bg-[#EBF5FB] text-[#2980B9] rounded shrink-0" style={{ fontWeight: 500 }}>
                                  Default
                                </span>
                              )}
                            </span>
                            <span className="block text-xs text-[#7F8C8D]">{roleLabel(org.role)}</span>
                          </span>
                        </DropdownMenuItem>
                      );
                    })}
                    {switchError && (
                      <p className="px-3 pt-2 pb-1 text-xs text-[#E74C3C]" style={{ fontWeight: 500 }}>
                        {switchError}
                      </p>
                    )}
                  </DropdownMenuContent>
                </DropdownMenu>
              )}
              <button
                className="relative p-2 hover:bg-gray-100 rounded-lg transition-colors"
                onClick={() => navigate('/messages')}
                title={unreadCount > 0 ? `${unreadCount} unread message${unreadCount !== 1 ? 's' : ''}` : 'Messages'}
              >
                <Bell size={24} className="text-[#7F8C8D]" />
                {/* Only show red dot when there are unread messages */}
                {unreadCount > 0 && (
                  <span className="absolute top-1 right-1 w-2 h-2 bg-[#E74C3C] rounded-full"></span>
                )}
              </button>
              <div className="flex items-center gap-2">
                <div className="w-10 h-10 bg-[#16A34A] rounded-full flex items-center justify-center text-white font-semibold">
                  {firstName.charAt(0).toUpperCase()}
                </div>
              </div>
            </div>
          </div>
        </header>

        {/* Page Content */}
        <div className="flex-1 p-4 md:p-8 overflow-auto">
          {children}
        </div>
      </main>
    </div>
  );
};

export default Layout;
